// src/admin/screens/borrowers/BorrowersListScreen.tsx
// Searchable roster of borrowers. The search term is bound to the URL
// (?q=…) so agents can share deep-links, and debouncing happens via
// react-query's query key — each keystroke invalidates the previous in-
// flight request at the useQuery layer.
//
// Visual matches the Thandizo Admin Console design: SectionHead + search
// pill + DataTable with avatar/borrower-name/borrower-id cell, KYC chip,
// score, and tier badge.
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, UserPlus } from 'lucide-react';
import { useBorrowers, type BorrowerRow } from '@admin/hooks/useBorrowers';
import DataTable, { type Column } from '@admin/components/DataTable';
import StatusChip from '@admin/components/StatusChip';
import TierBadge from '@admin/components/TierBadge';
import SectionHead from '@admin/components/SectionHead';
import Button from '@admin/components/Button';
import Avatar from '@admin/components/Avatar';
import { formatZambiaPhone } from '@admin/utils/phone';
import styles from './BorrowersListScreen.module.css';

function kycChip(status: BorrowerRow['kyc_status']) {
  switch (status) {
    case 'verified':
      return <StatusChip label="Verified" tone="positive" />;
    case 'rejected':
      return <StatusChip label="Rejected" tone="danger" />;
    case 'manual_review':
      return <StatusChip label="Manual review" tone="warn" />;
    case 'pending':
    default:
      return <StatusChip label="Pending" tone="neutral" />;
  }
}

export default function BorrowersListScreen() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const search = params.get('q') ?? '';
  const { data, isLoading, error } = useBorrowers(search);
  const rowCount = data?.length ?? 0;

  const cols: Column<BorrowerRow>[] = [
    {
      key: 'name',
      header: 'Borrower',
      render: (r) => {
        const name = r.display_name ?? r.full_name ?? '—';
        return (
          <div className={styles.borrowerCell}>
            <Avatar name={name} size={32} />
            <div>
              <div className={styles.borrowerName}>{name}</div>
              <div className={styles.borrowerId}>{r.id.slice(0, 8)}</div>
            </div>
          </div>
        );
      },
    },
    { key: 'phone', header: 'Phone', render: (r) => formatZambiaPhone(r.phone) },
    {
      key: 'email',
      header: 'Email',
      render: (r) => r.email ?? <span style={{ color: 'var(--c-text-dim)' }}>—</span>,
    },
    { key: 'kyc', header: 'KYC', render: (r) => kycChip(r.kyc_status) },
    {
      key: 'score',
      header: 'Score',
      align: 'right',
      render: (r) =>
        r.credit_score
          ? <span className="num" style={{ fontWeight: 600 }}>{r.credit_score.current_score}</span>
          : <span style={{ color: 'var(--c-text-dim)' }}>—</span>,
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (r) =>
        r.credit_score
          ? <TierBadge tier={r.credit_score.current_tier} />
          : <span style={{ color: 'var(--c-text-dim)' }}>—</span>,
    },
  ];

  return (
    <div className={styles.page}>
      <SectionHead
        eyebrow="Operations"
        title="Borrowers"
        description={`${rowCount} borrower${rowCount === 1 ? '' : 's'}${search ? ` matching "${search}"` : ''}`}
        action={<Button variant="primary" size="sm" leftIcon={<UserPlus size={14} />}>Onboard borrower</Button>}
      />

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Search size={15} />
          <input
            className={styles.search}
            placeholder="Search by name, phone, or email…"
            value={search}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              const v = e.target.value;
              if (v) next.set('q', v);
              else next.delete('q');
              setParams(next, { replace: true });
            }}
            aria-label="Search borrowers"
          />
        </div>
      </div>

      <DataTable
        rows={data}
        columns={cols}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={(error as Error | null)?.message ?? null}
        onRowClick={(r) => navigate(`/borrowers/${r.id}`)}
        emptyLabel={search ? 'No borrowers match that search.' : 'No borrowers yet.'}
      />
    </div>
  );
}
